import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Spinner, Alert, Row, Col, Nav, Modal } from 'react-bootstrap';
import {
    FaEye,
    FaDownload,
    FaUserTie,
    FaCalendarAlt,
    FaMapMarkerAlt,
    FaFileContract,
    FaExclamationTriangle,
    FaCheckCircle,
    FaClock,
    FaFilter,
    FaTimesCircle,
    FaUsers,
    FaFilePdf,
    FaSignature,
    FaTimes
} from 'react-icons/fa';
import { createClient } from '@supabase/supabase-js';
import HeaderTopSection from "../../components/partials/HeaderTop";
import ContractEmployeModal from '../../components/contract/ContractEmployeModalEmplyer';
import PDFViewerModal from '../../components/PDFViewerModal';
import ContractEmployeService from '../../services/ContractEmployeService';
import '../../assets/css/ContractsPage.css';
import Banner from "../../components/partials/Banner";
import EmployeSignatureModal from '../../components/contract/employe/EmployeSignatureModal';
import AdminSignatureModal from '../../components/contract/employe/AdminSignatureModal';
import AuthService from '../../services/AuthService';

// Configuration Supabase
const supabaseUrl = 'https://yrbhgwbzukfejvcphnwa.supabase.co';
const supabaseKey = 'sb_publishable_6drosvFnB3dDfyXC4X9XLg_cbhqx2S-';
const supabase = createClient(supabaseUrl, supabaseKey);

const MyContractEmployePage = () => {
    const [contracts, setContracts] = useState([]);
    const [filteredContracts, setFilteredContracts] = useState([]);
    const [activeFilter, setActiveFilter] = useState('PENDING_SIGNATURE_EMPLOYE_FALSE');
    const [selectedContract, setSelectedContract] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);
    const [signingId, setSigningId] = useState(null);
    const [generatingId, setGeneratingId] = useState(null);
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [pdfData, setPdfData] = useState(null);
    const [currentPdfUrl, setCurrentPdfUrl] = useState(null);
    const [showClientSignatureModal, setShowClientSignatureModal] = useState(false);
    const [contractToSign, setContractToSign] = useState(null);
    const [showAdminSignatureModal, setShowAdminSignatureModal] = useState(false);
    const [contractToAdminSign, setContractToAdminSign] = useState(null);
    const [showPdfViewer, setShowPdfViewer] = useState(false);
    const [selectedPdfUrl, setSelectedPdfUrl] = useState(null);
    const [selectedPdfContractId, setSelectedPdfContractId] = useState(null);
    const [currentProfessionalId, setCurrentProfessionalId] = useState(null);
    const [currentUserRole, setCurrentUserRole] = useState(null);

    useEffect(() => {
        // Récupérer l'utilisateur connecté
        const user = AuthService.getCurrentUser();
        if (user) {
            setCurrentProfessionalId(user.id);
            setCurrentUserRole(user.roles && user.roles.length > 0 ? user.roles[0] : null);
        }
    }, []);

    useEffect(() => {
        if (currentProfessionalId) {
            fetchContracts();
        }
    }, [currentProfessionalId]);

    useEffect(() => {
        filterContracts();
    }, [contracts, activeFilter]);

    const fetchContracts = async () => {
        if (!currentProfessionalId) {
            setIsLoading(false);
            setError('Utilisateur non connecté ou non professionnel');
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            // Récupérer tous les contrats
            const allContracts = await ContractEmployeService.getAllContractEmployes();

            // Filtrer pour n'afficher que les contrats du professionnel connecté
            const professionalContracts = allContracts.filter(contract => {
                // Plusieurs façons de vérifier selon la structure de vos données
                return contract.professionalId === currentProfessionalId ||
                    contract.professional?.id === currentProfessionalId ||
                    contract.professional?.user?.id === currentProfessionalId;
            });

            setContracts(professionalContracts);

            // Log pour débogage
            console.log('Contrats filtrés pour le professionnel:', {
                professionalId: currentProfessionalId,
                totalContracts: allContracts.length,
                filteredContracts: professionalContracts.length,
                contracts: professionalContracts
            });

        } catch (err) {
            console.error('Failed to fetch contracts:', err);
            setError('Échec du chargement des contrats employés. Veuillez réessayer.');
        } finally {
            setIsLoading(false);
        }
    };

    const filterContracts = () => {
        let filtered = [];

        switch (activeFilter) {
            case 'PENDING_SIGNATURE_EMPLOYE_FALSE':
                filtered = contracts.filter(contract =>
                    contract.contractEmployeStatus === 'PENDING_SIGNATURE_EMPLOYE' &&
                    !contract.clientSigned
                );
                break;

            case 'PENDING_SIGNATURE_EMPLOYE_TRUE':
                filtered = contracts.filter(contract =>
                    (contract.contractEmployeStatus === 'PENDING_SIGNATURE_EMPLOYE' && contract.clientSigned) ||
                    contract.contractEmployeStatus === 'PENDING_SIGNATURE_EMPLOYE_DHS'
                );
                break;

            case 'ACTIVE':
                filtered = contracts.filter(contract =>
                    contract.contractEmployeStatus === 'ACTIVE'
                );
                break;

            case 'COMPLETED':
                filtered = contracts.filter(contract =>
                    contract.contractEmployeStatus === 'TERMINATED' ||
                    contract.contractEmployeStatus === 'EXPIRED' ||
                    contract.contractEmployeStatus === 'CANCELLED'
                );
                break;

            default:
                filtered = contracts;
        }

        setFilteredContracts(filtered);
    };

    const handleFilterChange = (filter) => {
        setActiveFilter(filter);
    };

    const handleViewContract = (contract) => {
        setSelectedContract(contract);
        setIsModalOpen(true);
    };

    const handleViewActiveContract = (contract) => {
        if (contract.contractEmployeActive) {
            setSelectedPdfUrl(contract.contractEmployeActivePresigned);
            setSelectedPdfContractId(contract.contractRef);
            setShowPdfViewer(true);
        } else {
            alert('Aucun PDF disponible pour ce contrat actif');
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedContract(null);
    };

    const closePdfViewer = () => {
        setShowPdfViewer(false);
        setSelectedPdfUrl(null);
        setSelectedPdfContractId(null);
    };

    const handleDownloadPdf = async (contractId) => {
        setDownloadingId(contractId);
        try {
            await ContractEmployeService.generateContractEmployePdf(contractId);
        } catch (error) {
            console.error('Erreur lors du téléchargement:', error);
        } finally {
            setDownloadingId(null);
        }
    };

    const generateFileName = (contract) => {
        const contractId = contract.id;
        const clientName = getFullName(contract.client?.user).replace(/\s+/g, '-');
        const endDate = formatDateForFileName(contract.endDate);
        const randomSuffix = Math.floor(1000 + Math.random() * 9000); // 4 chiffres aléatoires

        return `CONTRACT-EMPLOYE-${contractId}-${clientName}-${endDate}-${randomSuffix}.pdf`;
    };

    const formatDateForFileName = (timestamp) => {
        if (!timestamp) return 'NO-DATE';
        const date = new Date(timestamp);
        return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()}`;
    };

    // Fonction pour obtenir le nom d'utilisateur connecté
    const getCurrentUser = () => {
        return localStorage.getItem('username') || 'admin';
    };

    const handleGenerateContract = async (contract) => {
        setGeneratingId(contract.id);
        try {
            // 1. Générer le PDF via l'API
            const pdfBytes = await ContractEmployeService.generateContractEmployePdf(contract.id);

            // 2. Convertir les bytes en Blob
            const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });

            // 3. Générer le nom du fichier
            const fileName = generateFileName(contract);
            const filePath = `contract/PENDING-SIGNATURE/${fileName}`;

            // 4. Upload vers Supabase Storage
            const { data, error } = await supabase.storage
                .from('driven-home-services')
                .upload(filePath, pdfBlob, {
                    cacheControl: '3600',
                    upsert: true
                });

            if (error) {
                throw new Error(`Erreur lors de l'upload: ${error.message}`);
            }

            // 5. Construire l'URL publique
            const publicUrl = `${supabaseUrl}/storage/v1/object/public/driven-home-services/${filePath}`;

            // 6. Mettre à jour le contrat employé en base de données
            const updateData = {
                contractEmployeStatus: 'PENDING_SIGNATURE_EMPLOYE',
                generatedContract: publicUrl,
                lastModifiedBy: getCurrentUser(),
            };

            // Appeler le service pour mettre à jour le contrat employé
            await ContractEmployeService.updateContractEmploye(contract.id, updateData);

            console.log('Contrat employé généré, sauvegardé et mis à jour avec succès:', {
                fileName,
                publicUrl,
                updateData
            });

            // 7. Afficher le PDF dans une modale
            const blobUrl = URL.createObjectURL(pdfBlob);
            setPdfData(blobUrl);
            setCurrentPdfUrl(publicUrl);
            setShowPdfModal(true);

            // 8. Rafraîchir la liste des contrats employés
            fetchContracts();

        } catch (error) {
            console.error('Erreur lors de la génération du contrat employé:', error);
            alert(`Erreur lors de la génération: ${error.message}`);
        } finally {
            setGeneratingId(null);
        }
    };

    // Fonction pour gérer la signature employé
    const handleSignContract = (contract) => {
        setContractToSign(contract);
        setShowClientSignatureModal(true);
    };

    // Fonction pour gérer la signature admin
    const handleAdminSignContract = (contract) => {
        setContractToAdminSign(contract);
        setShowAdminSignatureModal(true);
    };

    const closePdfModal = () => {
        setShowPdfModal(false);
        if (pdfData) {
            URL.revokeObjectURL(pdfData);
            setPdfData(null);
        }
        setCurrentPdfUrl(null);
    };

    const handleDownloadGeneratedPdf = () => {
        if (currentPdfUrl) {
            const link = document.createElement('a');
            link.href = currentPdfUrl;
            link.download = currentPdfUrl.split('/').pop() || 'contrat-employe.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'Non spécifié';
        return new Date(timestamp).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'PENDING_SIGNATURE':
                return <Badge bg="secondary" className="fs-6 py-2 px-3 rounded-pill">
                    <FaClock className="me-1" /> En attente génération
                </Badge>;
            case 'PENDING_SIGNATURE_EMPLOYE':
                return <Badge bg="warning" className="fs-6 py-2 px-3 rounded-pill">
                    <FaSignature className="me-1" /> En attente employé
                </Badge>;
            case 'PENDING_SIGNATURE_EMPLOYE_DHS':
                return <Badge bg="success" className="fs-6 py-2 px-3 rounded-pill">
                    <FaSignature className="me-1" /> Employé signé
                </Badge>;
            case 'ACTIVE':
                return <Badge bg="primary" className="fs-6 py-2 px-3 rounded-pill">
                    <FaCheckCircle className="me-1" /> Actif
                </Badge>;
            case 'EXPIRED':
                return <Badge bg="danger" className="fs-6 py-2 px-3 rounded-pill">
                    <FaTimesCircle className="me-1" /> Expiré
                </Badge>;
            case 'TERMINATED':
                return <Badge bg="success" className="fs-6 py-2 px-3 rounded-pill">
                    <FaCheckCircle className="me-1" /> Terminé
                </Badge>;
            case 'CANCELLED':
                return <Badge bg="dark" className="fs-6 py-2 px-3 rounded-pill">
                    <FaTimesCircle className="me-1" /> Annulé
                </Badge>;
            default:
                return <Badge bg="secondary" className="fs-6 py-2 px-3 rounded-pill">Inconnu</Badge>;
        }
    };

    const getAvatarUrl = (user) => {
        return user?.profilePictureUrl || 'https://yrbhgwbzukfejvcphnwa.supabase.co/storage/v1/object/public/driven-home-services/media-service/dhs.png';
    };

    const getFullName = (user) => {
        if (!user) return 'Information non disponible';
        return `${user.firstName} ${user.lastName}`.trim();
    };

    const getEmail = (user) => {
        return user?.email || 'Email non disponible';
    };

    const getPhone = (user) => {
        return user?.phoneNumber || 'Téléphone non disponible';
    };

    const getLocation = (user) => {
        if (!user) return 'Localisation non disponible';
        return `${user.city || ''}, ${user.country || ''}`.trim().replace(/^,\s*|\s*,$/g, '');
    };

    const getServiceLabel = (service) => {
        if (!service) return 'Service non spécifié';
        const serviceLabels = {
            'garde_enfant': 'Garde d\'enfant',
            'menage': 'Ménage',
            'cuisine': 'Cuisine',
            'jardinage': 'Jardinage'
        };
        return serviceLabels[service] || service.replace('_', ' ');
    };

    const getFilterStats = () => {
        return {
            PENDING_SIGNATURE_EMPLOYE_FALSE: contracts.filter(c =>
                c.contractEmployeStatus === 'PENDING_SIGNATURE_EMPLOYE' && !c.clientSigned
            ).length,
            PENDING_SIGNATURE_EMPLOYE_TRUE: contracts.filter(c =>
                (c.contractEmployeStatus === 'PENDING_SIGNATURE_EMPLOYE' && c.clientSigned) ||
                c.contractEmployeStatus === 'PENDING_SIGNATURE_EMPLOYE_DHS'
            ).length,
            ACTIVE: contracts.filter(c => c.contractEmployeStatus === 'ACTIVE').length,
            COMPLETED: contracts.filter(c =>
                ['TERMINATED', 'EXPIRED', 'CANCELLED'].includes(c.contractEmployeStatus)
            ).length
        };
    };

    const filterStats = getFilterStats();

    const getActionButton = (contract) => {
        switch (activeFilter) {
            case 'PENDING_SIGNATURE_EMPLOYE_FALSE':
                // Vérifier si l'utilisateur est bien le professionnel concerné
                if (currentProfessionalId && (
                    contract.professionalId === currentProfessionalId ||
                    contract.professional?.id === currentProfessionalId ||
                    contract.professional?.user?.id === currentProfessionalId
                )) {
                    return (
                        <Button
                            variant="success"
                            className="w-100"
                            onClick={() => handleSignContract(contract)}
                            size="lg"
                        >
                            <FaSignature className="me-2" /> Signer le contrat
                        </Button>
                    );
                }
                return null;

            case 'PENDING_SIGNATURE_EMPLOYE_TRUE':
                // CACHER le bouton "Signer le contrat" dans le bloc "À Signer par HDS"
                return null;

            case 'ACTIVE':
                return (
                    <Button
                        variant="success"
                        className="w-100"
                        onClick={() => handleViewActiveContract(contract)}
                        size="lg"
                    >
                        <FaEye className="me-2" /> Afficher le PDF
                    </Button>
                );

            default:
                return (
                    <Button
                        variant="success"
                        className="w-100"
                        onClick={() => handleViewActiveContract(contract)}
                        size="lg"
                    >
                        <FaEye className="me-2" /> Afficher le PDF
                    </Button>
                );
        }
    };

    return (
        <>
            <HeaderTopSection />
            <Banner
                title="Mes Contrats Employés"
                subtitle={`Consultez et gérez tous vos contrats employés ${currentProfessionalId ? `(Professionnel ID: ${currentProfessionalId})` : ''}`}
            />

            <div className="container-fluid py-4" style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
                <div className="container">
                    <div className="row">
                        {/* Sidebar de filtrage */}
                        <div className="col-lg-3 mb-4">
                            <Card className="shadow-sm border-0 sticky-top" style={{ top: '1px', borderRadius: '16px', overflow: 'hidden' }}>
                                <Card.Header className="bg-gradient text-white border-0 py-4" style={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                }}>
                                    <div className="d-flex align-items-center justify-content-center">
                                        <FaFilter className="me-2" size={20} />
                                        <h5 className="mb-0 fw-bold">Filtres des Contrats</h5>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <Nav variant="pills" className="flex-column">
                                        {/* À Signer */}
                                        <Nav.Item>
                                            <Nav.Link
                                                className={`filter-nav-link d-flex justify-content-between align-items-center py-3 px-4 ${activeFilter === 'PENDING_SIGNATURE_EMPLOYE_FALSE' ? 'active-pending' : ''}`}
                                                onClick={() => handleFilterChange('PENDING_SIGNATURE_EMPLOYE_FALSE')}
                                                style={{
                                                    cursor: 'pointer',
                                                    borderRadius: 0,
                                                    borderLeft: activeFilter === 'PENDING_SIGNATURE_EMPLOYE_FALSE' ? '4px solid #ffc107' : '4px solid transparent',
                                                    transition: 'all 0.3s ease',
                                                    backgroundColor: activeFilter === 'PENDING_SIGNATURE_EMPLOYE_FALSE' ? '#fff3cd' : 'transparent'
                                                }}
                                            >
                                                <span className="d-flex align-items-center">
                                                    <FaSignature className="me-2" style={{ color: activeFilter === 'PENDING_SIGNATURE_EMPLOYE_FALSE' ? '#ffc107' : '#6c757d' }} />
                                                    <span className={`fw-${activeFilter === 'PENDING_SIGNATURE_EMPLOYE_FALSE' ? 'bold' : 'medium'}`}>
                                                        À Signer
                                                    </span>
                                                </span>
                                                <Badge
                                                    bg={activeFilter === 'PENDING_SIGNATURE_EMPLOYE_FALSE' ? 'warning' : 'light'}
                                                    text={activeFilter === 'PENDING_SIGNATURE_EMPLOYE_FALSE' ? 'dark' : 'secondary'}
                                                    pill
                                                    className="px-3 py-2"
                                                >
                                                    {filterStats.PENDING_SIGNATURE_EMPLOYE_FALSE}
                                                </Badge>
                                            </Nav.Link>
                                        </Nav.Item>

                                        {/* En Attente HDS */}
                                        <Nav.Item>
                                            <Nav.Link
                                                className={`filter-nav-link d-flex justify-content-between align-items-center py-3 px-4 ${activeFilter === 'PENDING_SIGNATURE_EMPLOYE_TRUE' ? 'active-approved' : ''}`}
                                                onClick={() => handleFilterChange('PENDING_SIGNATURE_EMPLOYE_TRUE')}
                                                style={{
                                                    cursor: 'pointer',
                                                    borderRadius: 0,
                                                    borderLeft: activeFilter === 'PENDING_SIGNATURE_EMPLOYE_TRUE' ? '4px solid #28a745' : '4px solid transparent',
                                                    transition: 'all 0.3s ease',
                                                    backgroundColor: activeFilter === 'PENDING_SIGNATURE_EMPLOYE_TRUE' ? '#d4edda' : 'transparent'
                                                }}
                                            >
                                                <span className="d-flex align-items-center">
                                                    <FaSignature className="me-2" style={{ color: activeFilter === 'PENDING_SIGNATURE_EMPLOYE_TRUE' ? '#28a745' : '#6c757d' }} />
                                                    <span className={`fw-${activeFilter === 'PENDING_SIGNATURE_EMPLOYE_TRUE' ? 'bold' : 'medium'}`}>
                                                        En Attente HDS
                                                    </span>
                                                </span>
                                                <Badge
                                                    bg={activeFilter === 'PENDING_SIGNATURE_EMPLOYE_TRUE' ? 'success' : 'light'}
                                                    text={activeFilter === 'PENDING_SIGNATURE_EMPLOYE_TRUE' ? 'white' : 'secondary'}
                                                    pill
                                                    className="px-3 py-2"
                                                >
                                                    {filterStats.PENDING_SIGNATURE_EMPLOYE_TRUE}
                                                </Badge>
                                            </Nav.Link>
                                        </Nav.Item>

                                        {/* Contrats Actifs */}
                                        <Nav.Item>
                                            <Nav.Link
                                                className={`filter-nav-link d-flex justify-content-between align-items-center py-3 px-4 ${activeFilter === 'ACTIVE' ? 'active-completed' : ''}`}
                                                onClick={() => handleFilterChange('ACTIVE')}
                                                style={{
                                                    cursor: 'pointer',
                                                    borderRadius: 0,
                                                    borderLeft: activeFilter === 'ACTIVE' ? '4px solid #007bff' : '4px solid transparent',
                                                    transition: 'all 0.3s ease',
                                                    backgroundColor: activeFilter === 'ACTIVE' ? '#cce7ff' : 'transparent'
                                                }}
                                            >
                                                <span className="d-flex align-items-center">
                                                    <FaCheckCircle className="me-2" style={{ color: activeFilter === 'ACTIVE' ? '#007bff' : '#6c757d' }} />
                                                    <span className={`fw-${activeFilter === 'ACTIVE' ? 'bold' : 'medium'}`}>Contrats Actifs</span>
                                                </span>
                                                <Badge
                                                    bg={activeFilter === 'ACTIVE' ? 'primary' : 'light'}
                                                    text={activeFilter === 'ACTIVE' ? 'white' : 'secondary'}
                                                    pill
                                                    className="px-3 py-2"
                                                >
                                                    {filterStats.ACTIVE}
                                                </Badge>
                                            </Nav.Link>
                                        </Nav.Item>

                                        {/* Contrats Finalisés */}
                                        <Nav.Item>
                                            <Nav.Link
                                                className={`filter-nav-link d-flex justify-content-between align-items-center py-3 px-4 ${activeFilter === 'COMPLETED' ? 'active-rejected' : ''}`}
                                                onClick={() => handleFilterChange('COMPLETED')}
                                                style={{
                                                    cursor: 'pointer',
                                                    borderRadius: 0,
                                                    borderLeft: activeFilter === 'COMPLETED' ? '4px solid #6c757d' : '4px solid transparent',
                                                    transition: 'all 0.3s ease',
                                                    backgroundColor: activeFilter === 'COMPLETED' ? '#e9ecef' : 'transparent'
                                                }}
                                            >
                                                <span className="d-flex align-items-center">
                                                    <FaTimesCircle className="me-2" style={{ color: activeFilter === 'COMPLETED' ? '#6c757d' : '#6c757d' }} />
                                                    <span className={`fw-${activeFilter === 'COMPLETED' ? 'bold' : 'medium'}`}>Contrats Finalisés</span>
                                                </span>
                                                <Badge
                                                    bg={activeFilter === 'COMPLETED' ? 'secondary' : 'light'}
                                                    text={activeFilter === 'COMPLETED' ? 'white' : 'secondary'}
                                                    pill
                                                    className="px-3 py-2"
                                                >
                                                    {filterStats.COMPLETED}
                                                </Badge>
                                            </Nav.Link>
                                        </Nav.Item>
                                    </Nav>
                                </Card.Body>
                            </Card>
                        </div>

                        {/* Contenu principal */}
                        <div className="col-lg-9">
                            {isLoading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" size="lg" />
                                    <p className="mt-3 fs-5 text-muted">Chargement des contrats employés...</p>
                                </div>
                            ) : error ? (
                                <Alert variant="danger" className="mx-auto" style={{ maxWidth: '600px' }}>
                                    <div className="text-center">
                                        <FaExclamationTriangle size={32} className="mb-3 text-danger" />
                                        <h5 className="alert-heading">Erreur de chargement</h5>
                                        <p className="mb-3">{error}</p>
                                        <Button variant="primary" onClick={fetchContracts} size="lg">
                                            Réessayer le chargement
                                        </Button>
                                    </div>
                                </Alert>
                            ) : filteredContracts.length === 0 ? (
                                <div className="text-center py-5">
                                    <div className="py-5">
                                        <FaFileContract size={64} className="text-muted mb-4" />
                                        <h3 className="text-muted mb-3">
                                            {activeFilter === 'PENDING_SIGNATURE_EMPLOYE_FALSE' ? 'Aucun contrat à signer' :
                                                activeFilter === 'PENDING_SIGNATURE_EMPLOYE_TRUE' ? 'Aucun contrat en attente de HDS' :
                                                    activeFilter === 'ACTIVE' ? 'Aucun contrat actif' :
                                                        'Aucun contrat finalisé'}
                                        </h3>
                                        <Button variant="primary" size="lg" onClick={fetchContracts}>
                                            Actualiser la liste
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <>

                                    <Row xs={1} md={2} lg={3} className="g-4">
                                        {filteredContracts.map(contract => (
                                            <Col key={contract.id}>
                                                <Card className="h-100 shadow-lg border-0 hover-lift"
                                                      style={{
                                                          borderRadius: '16px',
                                                          transition: 'all 0.3s ease',
                                                          border: contract.contractEmployeStatus === 'ACTIVE' ? '2px solid #007bff' :
                                                              contract.contractEmployeStatus === 'PENDING_SIGNATURE_EMPLOYE' ? '2px solid #ffc107' :
                                                                  '2px solid #6c757d'
                                                      }}>
                                                    {/* Image du client */}
                                                    <div className="position-relative">
                                                        <Card.Img
                                                            variant="top"
                                                            src={getAvatarUrl(contract.client?.user)}
                                                            style={{
                                                                height: '180px',
                                                                objectFit: 'cover',
                                                                borderTopLeftRadius: '16px',
                                                                borderTopRightRadius: '16px'
                                                            }}
                                                            onError={(e) => {
                                                                e.target.src = 'https://yrbhgwbzukfejvcphnwa.supabase.co/storage/v1/object/public/driven-home-services/media-service/dhs.png';
                                                            }}
                                                        />
                                                        <div className="position-absolute top-0 end-0 m-3">
                                                            {getStatusBadge(contract.contractEmployeStatus)}
                                                        </div>
                                                    </div>

                                                    <Card.Body className="d-flex flex-column p-4">
                                                        {/* Informations du contrat */}
                                                        <div className="mb-3">
                                                            <div className="d-flex align-items-center mb-2">
                                                                <div className="bg-primary rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                                                                     style={{width: '40px', height: '40px'}}>
                                                                    <FaFileContract size={16} className="text-white" />
                                                                </div>
                                                                <div>
                                                                    <h6 className="mb-0 text-dark">Contrat: <span className="fw-bold">#{contract.contractRef}</span></h6>
                                                                    <small className="text-muted">
                                                                        {getServiceLabel(contract.professional?.selectedService)}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Informations des parties */}
                                                        <div className="mb-3">
                                                            <div className="d-flex align-items-center mb-2">
                                                                <div className="bg-success rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                                                                     style={{width: '32px', height: '32px'}}>
                                                                    <FaUserTie size={12} className="text-white" />
                                                                </div>
                                                                <div>
                                                                    <small className="text-dark fw-medium">Client</small>
                                                                    <br />
                                                                    <small className="text-muted">
                                                                        {getFullName(contract.client?.user)}
                                                                    </small>
                                                                </div>
                                                            </div>

                                                            <div className="d-flex align-items-center mb-2">
                                                                <div className="bg-info rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                                                                     style={{width: '32px', height: '32px'}}>
                                                                    <FaUserTie size={12} className="text-white" />
                                                                </div>
                                                                <div>
                                                                    <small className="text-dark fw-medium">Professionnel</small>
                                                                    <br />
                                                                    <small className="text-muted">
                                                                        {getFullName(contract.professional?.user)}
                                                                        {contract.professional?.user?.id === currentProfessionalId && (
                                                                            <Badge bg="info" className="ms-2">Vous</Badge>
                                                                        )}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </div>



                                                        {/* Informations supplémentaires */}
                                                        <div className="mb-3">

                                                            {contract.contractDuration && (
                                                                <Badge bg="info" className="mb-2">
                                                                    {contract.contractDuration} mois
                                                                </Badge>
                                                            )}
                                                        </div>

                                                        {/* Actions */}
                                                        <div className="mt-auto d-flex flex-column gap-2">
                                                            <Button
                                                                style={{background:'linear-gradient(135deg, rgb(23, 50, 71) 0%, rgb(60, 111, 205) 100%)'}}
                                                                className="w-100"
                                                                onClick={() => handleViewContract(contract)}
                                                                size="lg"
                                                            >
                                                                <FaEye className="me-2" /> Voir détails
                                                            </Button>

                                                            {getActionButton(contract)}
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal pour afficher les détails du contrat employé */}
            <ContractEmployeModal
                contract={selectedContract}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
            />

            {/* Modal pour afficher le PDF généré */}
            <Modal show={showPdfModal} onHide={closePdfModal} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaFilePdf className="me-2 text-danger" />
                        Contrat Employé Généré - #{selectedContract?.id}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="alert alert-success mb-3">
                        <FaCheckCircle className="me-2" />
                        Contrat employé généré avec succès ! Le statut a été mis à jour vers "En attente de signature employé".
                    </div>
                    <div className="pdf-viewer-container" style={{ height: '500px' }}>
                        {pdfData ? (
                            <iframe
                                src={pdfData}
                                title={`Contrat-Employe-${selectedContract?.id}`}
                                className="w-100 h-100 border-0"
                                style={{ minHeight: '500px' }}
                            />
                        ) : (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-3">Chargement du PDF...</p>
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closePdfModal}>
                        <FaTimes className="me-2" />
                        Fermer
                    </Button>
                    <Button variant="primary" onClick={handleDownloadGeneratedPdf}>
                        <FaDownload className="me-2" />
                        Télécharger le PDF
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal pour afficher les PDF actifs */}
            <PDFViewerModal
                show={showPdfViewer}
                onHide={closePdfViewer}
                pdfUrl={selectedPdfUrl}
                contractId={selectedPdfContractId}
                title="Contrat Employé Actif"
            />

            {/* Modal pour la signature employé */}
            <EmployeSignatureModal
                contract={contractToSign}
                isOpen={showClientSignatureModal}
                onClose={() => {
                    setShowClientSignatureModal(false);
                    setContractToSign(null);
                }}
                onSuccess={() => {
                    setShowClientSignatureModal(false);
                    setContractToSign(null);
                    fetchContracts(); // Rafraîchir la liste
                }}
            />

            {/* Modal pour la signature admin */}
            <AdminSignatureModal
                contract={contractToAdminSign}
                isOpen={showAdminSignatureModal}
                onClose={() => {
                    setShowAdminSignatureModal(false);
                    setContractToAdminSign(null);
                }}
                onSuccess={() => {
                    setShowAdminSignatureModal(false);
                    setContractToAdminSign(null);
                    fetchContracts(); // Rafraîchir la liste
                }}
            />
        </>
    );
};

export default MyContractEmployePage;